import { BASE_GROUND_OFFSET } from "../config";
import {
    BLOCK,
    TREE,
    type VoxelWorld,
    type BiomeMap,
    type Biome,
    type TreeType,
} from "../types";
import { BIOME_RULES, SURFACE } from "./biomeRules";
import { makeNoise2D } from "./heightmap";

const OAK_CROWN_RADIUS = 2;
const PINE_CROWN_RADIUS = 2;

enum OakLayerMode {
    Corners = "5x5_corners",
    Full = "3x3_full",
    Plus = "3x3_plus",
}

function pickTreeType(types: readonly TreeType[], n: number): TreeType {
    if (types.length === 1) return types[0];
    const idx = Math.floor(((n + 1) / 2) * types.length) % types.length;
    return types[idx];
}

/**
 * Minecraft-style oak: standard trunk 5-7 + 4-layer crown.
 * Two 5×5 lower layers (random corners) → 3×3 cap → 3×3 plus tip.
 */
function placeOak(world: VoxelWorld, x: number, z: number, topAbs: number, variation: number) {
    const trunkH = 5 + (variation % 3); // 5..7
    for (let i = 1; i <= trunkH; i++) {
        world.set(x, topAbs + i, z, BLOCK.Log);
    }
    const trunkTopY = topAbs + trunkH;

    const layers: { dy: number; r: number; mode: OakLayerMode }[] = [
        { dy: -1, r: 2, mode: OakLayerMode.Corners },
        { dy: 0, r: 2, mode: OakLayerMode.Corners },
        { dy: 1, r: 1, mode: OakLayerMode.Full },
        { dy: 2, r: 1, mode: OakLayerMode.Plus },
    ];

    for (const { dy, r, mode } of layers) {
        const ly = trunkTopY + dy;
        for (let dx = -r; dx <= r; dx++) {
            for (let dz = -r; dz <= r; dz++) {
                if (mode === OakLayerMode.Corners && Math.abs(dx) === 2 && Math.abs(dz) === 2) {
                    const h = (variation * 13 + dx * 7 + dz * 11 + dy * 17) & 1;
                    if (h === 0) continue;
                }
                if (mode === OakLayerMode.Plus && Math.abs(dx) === 1 && Math.abs(dz) === 1) continue;

                const rx = x + dx;
                const rz = z + dz;
                if (rx < 0 || rz < 0 || rx >= world.size || rz >= world.size) continue;
                if (rx === x && rz === z && world.get(x, ly, z) === BLOCK.Log) continue;
                if (!world.get(rx, ly, rz)) world.set(rx, ly, rz, BLOCK.Leaves);
            }
        }
    }
}

function placeGiantMushroom(
    world: VoxelWorld,
    x: number,
    z: number,
    topAbs: number,
    variation: number,
    type: typeof TREE.MushroomRed | typeof TREE.MushroomBrown,
) {
    const isRed = type === TREE.MushroomRed;
    const trunkH = (isRed ? 4 : 5) + (variation % 2);
    for (let i = 1; i <= trunkH; i++) {
        world.set(x, topAbs + i, z, BLOCK.MushroomStem);
    }
    const capY = topAbs + trunkH;
    const cap = isRed ? BLOCK.MushroomCapRed : BLOCK.MushroomCapBrown;

    const fillLayer = (ly: number, r: number, skipCorners: boolean) => {
        for (let dx = -r; dx <= r; dx++) {
            for (let dz = -r; dz <= r; dz++) {
                if (skipCorners && Math.abs(dx) === r && Math.abs(dz) === r) continue;
                const rx = x + dx;
                const rz = z + dz;
                if (rx < 0 || rz < 0 || rx >= world.size || rz >= world.size) continue;
                if (!world.get(rx, ly, rz)) world.set(rx, ly, rz, cap);
            }
        }
    };

    if (isRed) {
        fillLayer(capY, 2, true);
        fillLayer(capY + 1, 1, false);
    } else {
        fillLayer(capY + 1, 2, true);
    }
}

/**
 * Pine: tall trunk + 5 layers tapering from base to a single-leaf tip.
 */
function placePine(world: VoxelWorld, x: number, z: number, topAbs: number, variation: number) {
    const trunkH = 5 + (variation % 2); // 5..6
    for (let i = 1; i <= trunkH; i++) {
        world.set(x, topAbs + i, z, BLOCK.Log);
    }
    const baseY = topAbs + trunkH - 2; // crown starts 2 below trunk top
    const layers: number[] = [2, 2, 1, 1, 0]; // radius per layer, bottom → top
    for (let li = 0; li < layers.length; li++) {
        const ly = baseY + li;
        const radius = layers[li];
        for (let dx = -radius; dx <= radius; dx++) {
            for (let dz = -radius; dz <= radius; dz++) {
                const rx = x + dx;
                const rz = z + dz;
                if (rx < 0 || rz < 0 || rx >= world.size || rz >= world.size) continue;
                if (Math.abs(dx) + Math.abs(dz) > radius) continue;
                if (rx === x && rz === z && world.get(x, ly, z) === BLOCK.Log) continue;
                if (!world.get(rx, ly, rz)) world.set(rx, ly, rz, BLOCK.PineLeaves);
            }
        }
    }
}

export function placeTrees(world: VoxelWorld, heights: number[][], biomes: BiomeMap, seed: number) {
    const noise = makeNoise2D(seed * 3.1415 + 4242);
    const variationNoise = makeNoise2D(seed * 2.718 + 7777);
    const N = world.size;

    const bucketSize = 12;
    const trunkBuckets = new Map<number, { x: number; z: number; minDist: number }[]>();
    let maxPlacedDistance = 0;
    const bucketKey = (x: number, z: number) => x * N + z;
    const tooClose = (cx: number, cz: number, minDist: number): boolean => {
        const radius = Math.max(minDist, maxPlacedDistance);
        const minBucketX = Math.floor(Math.max(0, cx - radius) / bucketSize);
        const maxBucketX = Math.floor(Math.min(N - 1, cx + radius) / bucketSize);
        const minBucketZ = Math.floor(Math.max(0, cz - radius) / bucketSize);
        const maxBucketZ = Math.floor(Math.min(N - 1, cz + radius) / bucketSize);
        for (let bucketX = minBucketX; bucketX <= maxBucketX; bucketX++) {
            for (let bucketZ = minBucketZ; bucketZ <= maxBucketZ; bucketZ++) {
                const trunks = trunkBuckets.get(bucketKey(bucketX, bucketZ));
                if (!trunks) continue;
                for (const trunk of trunks) {
                    const dx = cx - trunk.x;
                    const dz = cz - trunk.z;
                    const limit = Math.max(minDist, trunk.minDist);
                    if (dx * dx + dz * dz < limit * limit) return true;
                }
            }
        }
        return false;
    };
    const indexTrunk = (x: number, z: number, minDist: number): void => {
        const key = bucketKey(Math.floor(x / bucketSize), Math.floor(z / bucketSize));
        const bucket = trunkBuckets.get(key);
        const trunk = { x, z, minDist };
        if (bucket) bucket.push(trunk);
        else trunkBuckets.set(key, [trunk]);
        maxPlacedDistance = Math.max(maxPlacedDistance, minDist);
    };

    for (let x = 0; x < N; x++) {
        for (let z = 0; z < N; z++) {
            const cellBiome: Biome = biomes.biome[x][z];
            const rules = BIOME_RULES[cellBiome];
            const treeCfg = rules.trees;
            if (!treeCfg || treeCfg.density <= 0 || treeCfg.types.length === 0) continue;

            const n = (noise(x / N, z / N) + 1) / 2;
            if (n > treeCfg.density) continue;

            const hRel = heights[x][z];
            const topAbs = BASE_GROUND_OFFSET + hRel;
            const topBlock = world.get(x, topAbs, z);
            const above = world.get(x, topAbs + 1, z);

            if (topBlock !== BLOCK.Grass && topBlock !== BLOCK.Mycelium) continue;
            if (above) continue;

            const tn = noise((x + 100) / N, (z - 100) / N);
            const type = pickTreeType(treeCfg.types, tn);
            const crownR = type === TREE.Pine ? PINE_CROWN_RADIUS : OAK_CROWN_RADIUS;

            if (x < crownR || z < crownR || x >= N - crownR || z >= N - crownR) continue;

            // Skip trees too close to water/banks. Iso projection shifts
            // elevated leaves diagonally in screen space, so canopies near a
            // river still appear to hang over the water even when the
            // footprint is on grass. A generous buffer keeps trees clearly
            // separated from the river and its sand banks visually.
            const liquidBuffer = crownR + 3;
            let nearLiquid = false;
            for (let dx = -liquidBuffer; dx <= liquidBuffer && !nearLiquid; dx++) {
                for (let dz = -liquidBuffer; dz <= liquidBuffer && !nearLiquid; dz++) {
                    const rx = x + dx;
                    const rz = z + dz;
                    if (rx < 0 || rz < 0 || rx >= N || rz >= N) continue;
                    const rTop = BASE_GROUND_OFFSET + heights[rx][rz];
                    const surfR = world.get(rx, rTop, rz);
                    const aboveR = world.get(rx, rTop + 1, rz);
                    if (aboveR === BLOCK.Water || aboveR === BLOCK.Ice) nearLiquid = true;
                    else if (surfR === BLOCK.Sand && rules.surface !== SURFACE.Sand) nearLiquid = true;
                }
            }
            if (nearLiquid) continue;

            // Crown footprint = 2*crownR+1. Add +3 buffer so canopies don't
            // touch and the silhouettes read as separate trees, not a hedge.
            const minDist = Math.max(treeCfg.minDistance, crownR * 2 + 3);
            if (tooClose(x, z, minDist)) continue;

            const variation = Math.floor(((variationNoise(x / N, z / N) + 1) / 2) * 1000);
            if (type === TREE.Oak) placeOak(world, x, z, topAbs, variation);
            else if (type === TREE.Pine) placePine(world, x, z, topAbs, variation);
            else placeGiantMushroom(world, x, z, topAbs, variation, type);

            indexTrunk(x, z, minDist);
        }
    }
}
